import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Skeleton } from "@material-ui/lab";
import companyPublicView from "../../../services/companyPublicView.services";
import _ from "lodash";
import appRoutes from "../../../routes/app.routes";
import { Button } from "../../../components/common";
import FeedList from "../../Feeds/FeedList";
import ArrowUp from "../../../components/ProfileViewNew/icons/arrow-up.svg";
import navigate from "../../../utils/history";

type ArticlesProps = {
  loading?: boolean;
};

const PREVIEW_LIMIT = 3;
const ARTICLE_POST_TYPE = 1;

const Articles = ({ loading: profileLoading = false }: ArticlesProps) => {
  const location = useLocation();
  const [articles, setArticles] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const { companyPublicProfile } = useSelector(
    ({ companyPublicView }: any) => companyPublicView.companyPublicView
  );

  const headerUser = useMemo(
    () => ({
      name: _.get(companyPublicProfile, "companyProfile.company_name", ""),
      profile_image: _.get(companyPublicProfile, "companyProfile.company_logo", ""),
      isCompany: true,
    }),
    [companyPublicProfile]
  );

  useEffect(() => {
    let cancelled = false;

    const loadArticles = async () => {
      const companyId = companyPublicProfile?.id;

      if (!companyId) {
        if (profileLoading) {
          setArticlesLoading(true);
          return;
        }
        if (!cancelled) {
          setArticles([]);
          setTotalRecords(0);
          setArticlesLoading(false);
        }
        return;
      }

      setArticlesLoading(true);
      try {
        const res: any = await companyPublicView.getCompanyFeeds({
          companyId,
          page: 1,
          pageSize: PREVIEW_LIMIT,
          postType: ARTICLE_POST_TYPE,
        });
        if (cancelled) return;
        const raw =
          res?.flag === false ? [] : _.get(res, "data", []);
        const list = Array.isArray(raw) ? raw : [];
        setArticles(list);
        setTotalRecords(_.get(res, "pagination.totalRecords", 0));
      } catch {
        if (!cancelled) {
          setArticles([]);
          setTotalRecords(0);
        }
      } finally {
        if (!cancelled) setArticlesLoading(false);
      }
    };

    loadArticles();

    return () => {
      cancelled = true;
    };
  }, [companyPublicProfile?.id, profileLoading, location.key]);

  const isLoading = profileLoading || articlesLoading;
  const companySlug = window.location.pathname.split("/")[2];
  const showSeeMore = totalRecords > PREVIEW_LIMIT;

  const handleSeeMore = () => {
    navigate(appRoutes.companyArticlesList.generatePath(companySlug));
  };

  if (isLoading) {
    return (
      <div className="company-home-section company-feed-section">
        <h2>Articles</h2>
        <div className="company-feed-list">
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={`article-skeleton-${item}`}
              variant="rect"
              width="100%"
              height={160}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (_.isEmpty(articles)) {
    return (
      <div className="company-home-section company-feed-section">
        <p>No articles found</p>
      </div>
    );
  }

  return (
    <div className="company-home-section company-feed-section">
      <div className="company-feed-list">
        <FeedList
          readOnly={true}
          feeds={articles}
          headerUser={headerUser}
          fetchOnScroll={false}
          pageSize={PREVIEW_LIMIT}
          feedArticle={true}
        />
        {showSeeMore && (
          <div className="btn-wrapper">
            <Button className="all-services-btn" onClick={handleSeeMore}>
              <span>See more</span>
              <img src={ArrowUp} alt="Arrow" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
